/**
 * エラーハンドリングユーティリティ
 * APIエラー整形・ログ出力・再スロー処理を共通化します
 */

import { logError } from './logger';

/** デフォルトのエラーコード */
const DEFAULT_ERROR_CODE = 'UNEXPECTED_ERROR';

/** デフォルトのHTTPステータス */
const DEFAULT_STATUS_CODE = 500;

/** アプリケーション共通エラーの追加情報 */
type AppErrorOptions = {
  code?: string;
  statusCode?: number;
  cause?: Error;
  details?: Record<string, unknown>;
};

/**
 * アプリケーション共通エラー
 */
export class AppError extends Error {
  /** エラーコード */
  code: string;
  /** HTTPステータス */
  statusCode: number;
  /** 元エラー */
  cause?: Error;
  /** 補足情報 */
  details: Record<string, unknown>;

  /**
   * 共通エラーを生成する
   * @param {string} message - エラーメッセージ
   * @param {Object} [options={}] - 追加情報
   * @param {string} [options.code='UNEXPECTED_ERROR'] - エラーコード
   * @param {number} [options.statusCode=500] - HTTPステータスコード
   * @param {Error | undefined} [options.cause] - 元エラー
   * @param {Object} [options.details={}] - 補足情報
   */
  constructor(message: string, options: AppErrorOptions = {}) {
    super(message);

    /** エラー名 */
    this.name = 'AppError';

    /** エラーコード */
    this.code = options.code ?? DEFAULT_ERROR_CODE;

    /** HTTPステータス */
    this.statusCode = options.statusCode ?? DEFAULT_STATUS_CODE;

    /** 元エラー */
    this.cause = options.cause;

    /** 補足情報 */
    this.details = options.details ?? {};
  }
}

/**
 * 任意のエラーをAppErrorへ正規化する
 * @param {unknown} error - 変換対象エラー
 * @param {string} [fallbackMessage='予期しないエラーが発生しました。'] - 既定メッセージ
 * @returns {AppError} 正規化済みエラー
 */
export const normalizeAppError = (
  error: unknown,
  fallbackMessage = '予期しないエラーが発生しました。',
): AppError => {
  if (error instanceof AppError) {
    return error;
  }

  if (error instanceof Error) {
    return new AppError(error.message || fallbackMessage, {
      cause: error,
    });
  }

  return new AppError(fallbackMessage, {
    details: { rawError: error },
  });
};

/**
 * APIレスポンス向けエラー形式へ整形する
 * @param {unknown} error - 変換対象エラー
 * @returns {{ error: { code: string, message: string, details: Object } }} API向けエラー
 */
export const formatApiError = (error: unknown) => {
  /** 正規化済みエラー */
  const appError = normalizeAppError(error);

  return {
    error: {
      code: appError.code,
      message: appError.message,
      details: appError.details,
    },
  };
};

/**
 * コンテキスト付きでエラーを記録し再スローする
 * @param {string} context - エラー発生コンテキスト
 * @param {unknown} error - 対象エラー
 * @param {Object} [metadata={}] - 追加メタ情報
 * @throws {AppError} 正規化済みエラー
 */
export const logAndRethrow = (
  context: string,
  error: unknown,
  metadata: Record<string, unknown> = {},
): never => {
  /** 正規化済みエラー */
  const appError = normalizeAppError(error);

  logError(`${context}: ${appError.message}`, {
    code: appError.code,
    details: appError.details,
    metadata,
  });

  throw appError;
};

/**
 * 非同期処理を共通エラーハンドリング付きで実行する
 * @template T
 * @param {string} context - 実行コンテキスト
 * @param {() => Promise<T>} handler - 実行対象処理
 * @returns {Promise<T>} 非同期処理結果
 * @throws {AppError} 正規化済みエラー
 */
export const withErrorHandling = async <T>(
  context: string,
  handler: () => Promise<T>,
): Promise<T> => {
  try {
    return await handler();
  } catch (error) {
    logAndRethrow(context, error);
  }
};
